import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { ElementRef, Injectable } from "@angular/core";
import { lastValueFrom, map } from "rxjs";
import { Account } from "sharedGuiSmall/sharedModels/account";
import { CountriesListDTO } from "sharedGuiSmall/sharedModels/countriesDto";
import { IndustriesDto } from "sharedGuiSmall/sharedModels/industriesDto";
import { PositionsData, PositionsListDTO } from "sharedGuiSmall/sharedModels/PositionsDto";
import { IResetPassword } from "sign-up-app/interfaces/Account/IResetPassword";
import { GetTokenData } from "sign-up-app/interfaces/GetTokenData";
import { ValidationMessage } from "sign-up-app/interfaces/ValidationMessage";
import { AccessToken } from "sign-up-app/login/AccessToken";
import { AuthData } from "sharedFeatures/sharedModels/AuthData";
import { IErrorResponse, isSuccessResponse } from "sharedFeatures/sharedServices/Responses";
import { IUserDetailsResponse } from "sharedFeatures/sharedServices/Responses/Auth/IUserDetailsResponse";
import { ICheckUserInviteResponse } from "sharedFeatures/sharedServices/Responses/Teams/ICheckUserInviteResponse";
import { LicenseStatus } from "sharedGuiSmall/enums";
import { RegisterOrganisation } from "../model/RegisterOrganisation";

declare let handleAngularHttpError: any;

@Injectable()
export class AuthAppService {

    fullUrl: string = window.fullLiveUrl;
    accountUrl: string = "Account";
    public Account: Account = new Account();

    positionsPromise: Promise<PositionsListDTO> | undefined;
    positions: PositionsData | undefined;
    industriesPromise: Promise<CountriesListDTO> | undefined;
    industries: IndustriesDto | undefined;

    constructor(
        private http: HttpClient) {
    }

    getAccountUrl(): string {
        return this.fullUrl + this.accountUrl;
    }

    toAccount(): void {
        document.location.assign(this.getAccountUrl());
    }

    LoadPositions(): void {
        let params = new HttpParams()
            .set('userID', 0);

        const url = `${window.coreApiUrl}Profile/LoadPositions`;
        const response = this.http.get<string>(url, { params });
        
        this.positionsPromise = lastValueFrom(response)
            .then((res: string) => {
                let data = JSON.parse(res);
                this.positions = data;
                return data;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('Profile/LoadPositions error', error);

                handleAngularHttpError(error);
            });
    }

    LoadIndustries(): void {
        let params = new HttpParams()
            .set('userID', 0);

        const url = `${window.coreApiUrl}Profile/LoadIndustries`;
        const response = this.http.get<string>(url, { params });

        this.industriesPromise = lastValueFrom(response)
            .then((res: string) => {
                let data = JSON.parse(res);
                this.industries = data;
                return data;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('Profile/LoadIndustries error', error);

                handleAngularHttpError(error);
            });
    }

    GetToken(url: string, data: GetTokenData): Promise<AccessToken> {
        const headers = new HttpHeaders({
            'Authorization': 'Basic ' + btoa('brixx.frontend' + ":" + 'brixx.frontend.secret'),
            'Content-Type': 'application/x-www-form-urlencoded',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'POST, PUT, GET, OPTIONS',
            'Access-Control-Allow-Headers': 'Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With',
        });

        let body = "";
        for (let key in data) {
            if (body.length)
                body += "&";

            body += key + "=";
            body += encodeURIComponent(data[key]);
        }

        const response = this.http.post<AccessToken>(url, body,{ headers: headers });
        
        return lastValueFrom(response)
            .then((res: AccessToken) => {return res;})
            .catch((error: HttpErrorResponse) => {
                console.error('GetToken error', error);

                handleAngularHttpError(error);
                
                throw error;
            });
    }

    checkResetPasswordError(resetCode: string){
        const response = this.http.post<string>(window.appPath + 'Account/CheckResetPasswordError', { resetCode: resetCode });
        return lastValueFrom(response)
            .then((res: string) => {
                const result = JSON.parse(res) as IResetPassword;
                return result.resetPasswordExpired;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('ChangeSortType error', error);

                handleAngularHttpError(error);

                //if we need to transmit the error to a component
                throw error;
            });
    }

    OnSSOLogin(url: string, data: object): Promise<string> {
        const response = this.http.post<string>(url, data);
        
        return lastValueFrom(response)
            .then((res: string) => {return res;})
            .catch((error: HttpErrorResponse) => {
                console.error('GetToken error', error);

                handleAngularHttpError(error);

                throw error;
            });
    }

    CheckVerificationEmail(email: string): Promise<string> {

        const url = `${window.appPath}SignUp/CheckVerificationEmail`;
        var data = { email: email };

        const response = this.http.post<string>(url, data);

        return lastValueFrom(response)
            .then((res: string) => { return res; })
            .catch((error: HttpErrorResponse) => {
                console.error('GetToken error', error);

                handleAngularHttpError(error);

                throw error;
            });
    }

    SignUpWithXero(positionId: number, getNews: boolean, firstName: string, lastName: string): Promise<string> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const data = { selectedPositionId: positionId, getNews: getNews, integrationTag: 'Xero', firstName: firstName, lastName: lastName };
        const url = `${window.appPath}SignUp/RegisterIntegrationCompanyUser`;

        const response = this.http.post<string>(url, data, { headers: headers });
        return lastValueFrom(response)
            .then((res: string) => {return res;})
            .catch((error: HttpErrorResponse) => {
                console.error('RegisterIntegrationCompanyUser error', error);

                handleAngularHttpError(error);

                throw error;
            });
    }

    RegisterUser(data: string): Promise<string> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${window.appPath}Auth/RegisterUser`;

        const response = this.http.post<string>(url, data, { headers: headers });
        return lastValueFrom(response)
            .then((res: string) => {return res;})
            .catch((error: HttpErrorResponse) => {
                console.error('RegisterUser error', error);

                handleAngularHttpError(error);
                
                throw error;
            });
    }

    CreateTemporaryLicense(packageId: string, orderRef: string) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${window.appPath}Auth/CreateTemporaryLicense`;

        let companyId = localStorage.getItem('selectedCompanyId');
        
        const data = { companyId: companyId, packageId: packageId, orderRef }

        const response = this.http.post<string>(url, data, { headers: headers });
        return lastValueFrom(response)
            .then((res: string) => {
                return res;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('CreateTemporaryLicense error', error);

                handleAngularHttpError(error);

                throw error;
            });
     }
    
    GetPricesTable(productCode: string): Promise<any> {
        let params = new HttpParams();
        const isGetTablePrices = true;
        params = params
            .set('productCode', productCode)

        const response = this.http.get<string>(`${window.appPath}/SignUp/GetPriceTable`, { params });
        return lastValueFrom(response)
            .then((res: string) => {
                if (res && res.length) {
                    console.log('GetPricesTable ', JSON.parse(res));
                    return JSON.parse(res);
                }
            });
    }

    SetupOrganisation(
        customerId: number,
        newUserEmail: string,
        packageType: string,
        isPaidSignup: boolean,
        industryId: number = 149, // default other
        orgName: string = 'My Plans', // default
        selectedPositionId: number = 7, // default other        
    ): Promise<string> {

        const organisation: RegisterOrganisation = {
            countryId: 1,
            customerId: customerId,
            email: newUserEmail,
            industryId: industryId, 
            orgName: orgName,
            selectedPositionId: selectedPositionId,
            isFree: packageType != LicenseStatus.Trial && !isPaidSignup
        }

        const data = JSON.stringify(organisation);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${window.appPath}Auth/SetupOrganisation`;
        const response = this.http.post<string>(url, data, { headers: headers });
        return lastValueFrom(response)
            .then((res: string) => { return res; })
            .catch((error: HttpErrorResponse) => {
                console.error('RegisterUser error', error);

                handleAngularHttpError(error);

                throw error;
            });
    }

    getAccountDataMiddleware(): Promise<Account> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${window.appPath}Account/AccountSettingsJson`;
        
        const response = this.http.get<string>(url, { });
        return lastValueFrom(response)
            .then((res: string) => {
                this.Account = JSON.parse(res) as Account;
                return this.Account;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('AccountSettingsJson error', error);

                handleAngularHttpError(error);

                throw error;
            });
        }

    SendPasswordReset(data: string): Promise<string> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${window.appPath}Auth/PasswordForgetRequest`;

        const response = this.http.post<string>(url, data, { headers: headers });
        return lastValueFrom(response)
            .then((res: string) => {return res;})
            .catch((error: HttpErrorResponse) => {
                console.error('PasswordForgetRequest error', error);

                handleAngularHttpError(error);

                throw error;
            });
    }

    PasswordReset(data: string): Promise<string> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${window.appPath}Auth/PasswordResetConfirm`;
        
        const response = this.http.post<string>(url, data, { headers: headers });
        return lastValueFrom(response)
            .then((res: string) => {return res;})
            .catch((error: HttpErrorResponse) => {
                console.error('PasswordResetConfirm error', error);

                handleAngularHttpError(error);

                throw error;
            });
    }
    
    get isSpinnerHidden() {
        let spinner = document.getElementById('spinner');
        return !spinner || spinner.style.getPropertyValue('display') === 'none';
    }

    toggleSpinner(): void {
        let spinner = document.getElementById('spinner');
        if (spinner) {
            if (spinner.style.getPropertyValue('display') === 'none') {
                spinner.style.setProperty('z-index', '1');
                spinner.style.removeProperty('display');
            } else
                spinner.style.setProperty('display', 'none');
        }
    }

    AddErrorMessage(input: ElementRef, errorMsgElem: ElementRef, message: string, validateMarkOk: string = 'validation-ok-mark', validateMarkFail: string = 'validation-failed-mark'): void {
        if (input && input.nativeElement) {
            input.nativeElement.classList.add(validateMarkFail);
            input.nativeElement.classList.remove(validateMarkOk);
        }

        errorMsgElem.nativeElement.innerText = message;
    }

    ValidateField(input: ElementRef | undefined, errorMsgElem: ElementRef, 
                  value: string, validations: Array<string>, 
                  validationMessages: ValidationMessage, optional='', 
                  validateMarkOk: string = 'validation-ok-mark', 
                  validateMarkFail: string = 'validation-failed-mark'): boolean {

        for (let i = 0; i < validations.length; i++) {

            let isValid = true;
            let validationType = validations[i].split(' ');
            let message = validationMessages[validationType[0]];

            switch (validationType[0]) {
                case 'required':  isValid = this.ValidateRequired(value);
                    break;
                case 'minlength': isValid = this.ValidateMinLength(value, parseInt(validationType[1]));
                    break;
                case 'uppercase': isValid = this.ValidateUpperCase(value);
                    break;
                case 'lowercase': isValid = this.ValidateLowerCase(value);
                    break;
                case 'special': isValid = this.ValidateSpecial(value);
                    break;
                case 'numbers': isValid = this.ValidateNumbers(value);
                    break;
                case 'email': isValid = this.ValidateEmail(value);
                    break;
                case 'maxlength': isValid = this.ValidateMaxLength(value, parseInt(validationType[1]));
                    break;
                case 'confirmemail': isValid = this.ValidateConfirmEmail(value, optional);
                    break;
                case 'confirmpassword': isValid = this.ValidateConfirmPassword(value, optional);
                    break;
            }

            if (!isValid) {
                if (input && input.nativeElement) {
                    input.nativeElement.classList.add(validateMarkFail);
                    input.nativeElement.classList.remove(validateMarkOk);
                }

                errorMsgElem.nativeElement.innerText = message;

                return false;
            } else {
                if (input && input.nativeElement) {
                    input.nativeElement.classList.add(validateMarkOk);
                    input.nativeElement.classList.remove(validateMarkFail);
                }

                errorMsgElem.nativeElement.innerText = '​'; //zero-width space
            }
        }

        return true;
    }

    ValidateRequired(value: string): boolean {
        if (!value) return false;
        return value != '0' && value.length > 0;
    }

    ValidateSpecial(value: string): boolean {
        let re = /[*@!#%&()^~{}]+|[0-9]/;
        return re.test(value);
    }

    ValidateNumbers(value: string): boolean {
        let r = value.match(/[0-9]/);
        return r != null;
    }

    ValidateUpperCase(value: string): boolean {
        let r = value.match(/[A-Z]/);
        return r != null;
    }

    ValidateLowerCase(value: string): boolean {
        let r = value.match(/[a-z]/);
        return r != null;
    }

    ValidateMinLength(value: string, minlength: number): boolean {
        if (!value) return false;

        if (isNaN(minlength)) return false;

        return value.length >= minlength;
    }

    ValidateMaxLength(value: string, maxlength: number): boolean {
        if (!value) return false;

        if (isNaN(maxlength)) return false;

        return value.length <= maxlength;
    }

    ValidateEmail(value: string): boolean {
        let emailRegxp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegxp.test(value);
    }

    ValidateConfirmEmail(email: string, confirmEmail: string): boolean {
        return email === confirmEmail;
    }

    ValidateConfirmPassword(password: string, confirmPassword: string): boolean {
        return password === confirmPassword;
    }

    ProcessPasswordHints(value: string, pwdHints: ElementRef, length: number) {
        if (pwdHints.nativeElement.classList.contains("hideHints"))
            pwdHints.nativeElement.classList.remove("hideHints");

        if (this.ValidateLowerCase(value)) {
            if (!pwdHints.nativeElement.classList.contains("lowercaseHint"))
                pwdHints.nativeElement.classList.add("lowercaseHint");
        } else
            pwdHints.nativeElement.classList.remove("lowercaseHint");

        if (this.ValidateUpperCase(value)) {
            if (!pwdHints.nativeElement.classList.contains("uppercaseHint"))
                pwdHints.nativeElement.classList.add("uppercaseHint");
        } else
            pwdHints.nativeElement.classList.remove("uppercaseHint");

        if (this.ValidateMinLength(value, length)) {
            if (!pwdHints.nativeElement.classList.contains("minLengthHint"))
                pwdHints.nativeElement.classList.add("minLengthHint");
        } else
            pwdHints.nativeElement.classList.remove("minLengthHint");

        if (this.ValidateSpecial(value)) {
            if (!pwdHints.nativeElement.classList.contains("specialHint"))
                pwdHints.nativeElement.classList.add("specialHint");
        } else
            pwdHints.nativeElement.classList.remove("specialHint");
    }
    
    ProcessPasswordSwitcher(passwordElement: ElementRef, pwdEye: ElementRef){
        if (passwordElement.nativeElement.type === "password") {
            passwordElement.nativeElement.type = "text";
            pwdEye.nativeElement.classList.add('eye_password_show');
            pwdEye.nativeElement.classList.remove('eye_password_hint');
        } else {
            passwordElement.nativeElement.type = "password";
            pwdEye.nativeElement.classList.add('eye_password_hint');
            pwdEye.nativeElement.classList.remove('eye_password_show');
        }
    }

    getAuthData() {
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-XSRF-Token', (<HTMLInputElement>document.getElementsByName('__RequestVerificationToken')[0]).value);
        const nameObj = {
            doHandleLastPlan: false,
            isFromAccount: false
        };
        return lastValueFrom(this.http.post<string>(window.appPath + 'Auth/CheckIsLoggedIn', nameObj, { headers: headers })
            .pipe(map(resp => JSON.parse(resp) as AuthData)));
    }

    isUserHasInvites(email: string) {

        const url = `${window.appPath}Team/CheckUserHasInvite`;
        const queryParams = { "email": email };
        const response = this.http.get<string>(url, { params: queryParams });

        return lastValueFrom(response)
            .then((res) => {
                const result: ICheckUserInviteResponse | IErrorResponse = JSON.parse(res);
                if (isSuccessResponse(result)) {
                    return result.data;
                } else
                    return false;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('Team/CheckUserHasInvite error', error);

                handleAngularHttpError(error);
                throw error;
            });
    }

    getCurrentUser() {

        const url = `${window.appPath}Auth/GetCurrentUser`;
        const response = this.http.get<string>(url);

        return lastValueFrom(response)
            .then((res) => {
                const result: IUserDetailsResponse | IErrorResponse = JSON.parse(res);
                return result;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('Auth/GetCurrentUser error', error);

                handleAngularHttpError(error);
                throw error;
            });
    }

    resendVerificationEmail(email: string) {
        const url = `${window.appPath}Account/ResendVerificationEmail`;

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')        
        
        const queryParams = { "email": email };
        const response = this.http.post<string>(url, queryParams, { headers: headers });

        return lastValueFrom(response)
            .then((res) => {
                const result: ICheckUserInviteResponse = JSON.parse(res);
                if (isSuccessResponse(result)) {
                    return result.data;
                } else
                    return false;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('Account/ResendVerificationEmail error', error);

                handleAngularHttpError(error);
                throw error;
            });
    }
}